import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { charmGray, destructive, grayIconFill, midGrey } from '../../../../web/styles/colors';

/**
 * Indentation levels for the nested hierarchy
 * Category: 0px, SkillArea: 24px, Scenario: 48px
 */
export const INDENT_LEVEL = 24;

export const styleSheet = StyleSheet.create({
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		padding: 40,
	},
	emptyState: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		padding: 40,
		color: midGrey,
		fontSize: 14,
		gap: 16,
	},
	errorState: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		padding: 40,
		color: midGrey,
		fontSize: 14,
	},
	rowsContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: 0,
	},
	// Base row styles - applied to all row types
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		padding: '12px 16px',
		borderBottom: `1px solid ${charmGray}`,
		transition: 'background-color 0.2s',
		':hover': {
			backgroundColor: THEME.colors.hover.light,
		},
	},
	// Category row (level 0) - no indent
	categoryRow: {
		paddingLeft: 16,
		backgroundColor: THEME.colors.background.main,
		fontWeight: 600,
	},
	// Skill area row (level 1) - 24px indent
	skillAreaRow: {
		paddingLeft: 16 + INDENT_LEVEL,
	},
	// Scenario row (level 2) - 48px indent
	scenarioRow: {
		paddingLeft: 16 + INDENT_LEVEL * 2,
	},
	// Chevron icon container
	chevronContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 20,
		height: 20,
		cursor: 'pointer',
		flexShrink: 0,
	},
	// Chevron icon
	chevron: {
		width: 16,
		height: 16,
		fill: grayIconFill,
		transition: 'transform 0.2s ease-in-out',
	},
	// Chevron expanded state
	chevronExpanded: {
		transform: 'rotate(90deg)',
	},
	// Placeholder for items without chevron (scenarios)
	chevronPlaceholder: {
		width: 20,
		height: 20,
		flexShrink: 0,
	},
	// Bullet indicator for scenarios
	bulletIndicator: {
		width: 20,
		height: 20,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: grayIconFill,
		fontSize: 16,
		flexShrink: 0,
	},
	// Row content - name and description
	rowContent: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		minWidth: 0,
	},
	rowName: {
		color: THEME.colors.text.dark,
		fontSize: 14,
		fontWeight: 500,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	rowDescription: {
		color: midGrey,
		fontSize: 12,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	// Status badge
	statusBadge: {
		padding: '2px 8px',
		borderRadius: 4,
		fontSize: 11,
		fontWeight: 500,
		textTransform: 'uppercase',
		flexShrink: 0,
	},
	statusActive: {
		backgroundColor: 'rgba(76, 175, 80, 0.1)',
		color: '#4caf50',
	},
	statusInactive: {
		backgroundColor: 'rgba(158, 158, 158, 0.1)',
		color: '#9e9e9e',
	},
	// Role badge (for categories)
	roleBadge: {
		padding: '2px 8px',
		borderRadius: 4,
		fontSize: 11,
		fontWeight: 500,
		backgroundColor: 'rgba(63, 81, 181, 0.1)',
		color: '#3f51b5',
		flexShrink: 0,
	},
	// Action buttons container - always visible
	actions: {
		display: 'flex',
		gap: 8,
		alignItems: 'center',
		flexShrink: 0,
	},
	// Nested content container (for expanded items)
	nestedContent: {
		display: 'flex',
		flexDirection: 'column',
		gap: 0,
	},
	// Count badge (shows number of children)
	countBadge: {
		padding: '2px 8px',
		borderRadius: 12,
		fontSize: 11,
		fontWeight: 500,
		backgroundColor: charmGray,
		color: grayIconFill,
		flexShrink: 0,
	},
	// Form styles (used by CategoryForm, SkillAreaForm, ScenarioForm)
	formGroup: {
		marginBottom: 20,
		':last-child': {
			marginBottom: 0,
		},
	},
	label: {
		display: 'block',
		fontSize: 14,
		fontWeight: 600,
		marginBottom: 8,
		color: THEME.colors.primary.main,
	},
	required: {
		color: destructive,
	},
	checkboxLabel: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		cursor: 'pointer',
	},
	checkboxText: {
		fontSize: 14,
		color: THEME.colors.primary.main,
	},
	error: {
		display: 'block',
		color: destructive,
		fontSize: 12,
		marginTop: 4,
	},
});
